// Hook d'enregistrement automatique partagé (Prompt 10 §A.1).
// L'état local est la source de vérité tant que le champ a le focus : le serveur ne
// réécrit jamais un champ en cours d'édition. Debounce 700 ms, requêtes numérotées
// (réponses obsolètes ignorées), envoi immédiat à la perte du focus.
import { useEffect, useRef, useState } from "react";

export function useAutoSaveValue(serverValue, save, delay = 700) {
  const [value, setValue] = useState(serverValue ?? "");
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const focused = useRef(false);
  const timer = useRef(null);
  const seq = useRef(0);
  const lastSent = useRef(0);

  useEffect(() => {
    if (!focused.current && (serverValue ?? "") !== value) setValue(serverValue ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValue]);

  const commit = (v) => {
    const mySeq = ++seq.current; lastSent.current = mySeq; setStatus("saving");
    Promise.resolve(save(v))
      .then(() => { if (mySeq === lastSent.current) setStatus("saved"); })
      .catch(() => { if (mySeq === lastSent.current) setStatus("error"); });
  };
  const onChange = (v) => { setValue(v); clearTimeout(timer.current); timer.current = setTimeout(() => commit(v), delay); };
  const onFocus = () => { focused.current = true; };
  const onBlur = () => { focused.current = false; clearTimeout(timer.current); commit(value); };
  return { value, setValue, status, onChange, onFocus, onBlur };
}

// Petit indicateur d'état d'enregistrement.
export function SaveStatus({ status, className }) {
  const map = { saving: "Enregistrement…", saved: "Enregistré · à l'instant", error: "Échec · Réessayer" };
  if (!status || status === "idle") return null;
  return <span className={className} style={{ fontSize: 11.5, color: status === "error" ? "#dc2626" : "#8a8f9c" }}>{map[status]}</span>;
}

// Champ texte auto-sauvegardé.
export function AutoInput({ serverValue, onSave, ...rest }) {
  const a = useAutoSaveValue(serverValue, onSave);
  return <input {...rest} value={a.value} onChange={(e) => a.onChange(e.target.value)} onFocus={a.onFocus} onBlur={a.onBlur} />;
}

// Zone de texte auto-sauvegardée (renvoie aussi le status via children render prop optionnelle).
export function AutoTextarea({ serverValue, onSave, renderStatus, ...rest }) {
  const a = useAutoSaveValue(serverValue, onSave);
  return (
    <>
      <textarea {...rest} value={a.value} onChange={(e) => a.onChange(e.target.value)} onFocus={a.onFocus} onBlur={a.onBlur} />
      {renderStatus && <SaveStatus status={a.status} />}
    </>
  );
}
